import { Disc3 } from "lucide-react";
import { useState } from "react";

import { cn, focusRingClassName, interactiveMotionClassName } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ProjectCoverTransition } from "@/music-catalog/components/project-cover-transition";
import {
  resolveProjectArtworkPanels,
  resolveProjectArtworkViews,
} from "@/music-catalog/utils/project-artwork";

import type { LibraryProject } from "@/cloud/types/types";
import type {
  ProjectArtworkPanelId,
  ProjectArtworkViewId,
} from "@/music-catalog/utils/project-artwork";

/**
 * A project's artwork, either as a preview of the case or as one isolated
 * piece.
 *
 * Preview places the Front over the Disc, the way the pieces would lie on a
 * table. The Back is not part of that arrangement: it, the Front, and the Disc
 * each have their own view for looking at one piece without another behind it.
 *
 * Every piece uses `object-contain`, because this application is told none of
 * its dimensions. Cropping would mean guessing at what the user drew.
 */
export const ProjectArtwork = ({ project }: { project: LibraryProject }) => {
  const { t } = useTranslation();
  const [selectedViewId, setSelectedViewId] = useState<ProjectArtworkViewId | undefined>(undefined);
  /**
   * The pictures the browser turned out not to be able to draw.
   *
   * A picture is named by digest, and the object behind a digest can have been
   * released or never have arrived. A failed piece leaves every view that needs
   * it, rather than sitting in either as a broken image.
   */
  const [failedPanelIds, setFailedPanelIds] = useState<ReadonlySet<ProjectArtworkPanelId>>(
    new Set(),
  );

  const panels = resolveProjectArtworkPanels(project).filter(
    (panel) => !failedPanelIds.has(panel.id),
  );
  const views = resolveProjectArtworkViews(panels);
  // Preview is first whenever it can be drawn. A selection whose picture has
  // since failed falls back to the first remaining view.
  const selectedView = views.find((view) => view.id === selectedViewId) ?? views[0];
  const hasPreview = views.some((view) => view.id === "preview");

  const markFailed = (id: ProjectArtworkPanelId) =>
    setFailedPanelIds((failed) => new Set(failed).add(id));

  if (selectedView === undefined) {
    return (
      <div
        role="img"
        aria-label={t("project.artwork.none")}
        className="flex aspect-square w-56 shrink-0 items-center justify-center rounded-2xl border border-border/60 bg-muted/60 text-muted-foreground sm:w-72"
      >
        <Disc3 className="size-16" aria-hidden />
      </div>
    );
  }

  const selectedLabel = t(selectedView.labelKey);
  const selectedAlt = t("project.artwork.alt", {
    artwork: selectedLabel,
    title: project.title,
  });

  // One piece on its own. Only the Front travels between pages: a Back or a Disc
  // arriving where the catalog's cover was would be a different picture flying
  // into its place.
  const singlePanel = (
    <img
      src={selectedView.panels[0].url}
      alt={selectedAlt}
      className={cn(
        "relative mx-auto block object-contain lg:mx-0",
        hasPreview ? "w-[72%]" : "w-full",
        selectedView.id !== "back" && "aspect-square",
        selectedView.id === "back" &&
          hasPreview &&
          "absolute inset-x-0 top-1/2 -translate-y-1/2 lg:right-auto lg:left-0",
        selectedView.id === "disc"
          ? "rounded-full shadow-2xl"
          : "rounded-[2px] shadow-[0_25px_50px_-12px_rgb(0_0_0/0.7)] ring-1 ring-black/25",
      )}
      onError={() => markFailed(selectedView.panels[0].id)}
    />
  );
  const isolatedPiece =
    selectedView.id === "front" ? (
      <ProjectCoverTransition desktopProjectId={project.desktopProjectId}>
        {singlePanel}
      </ProjectCoverTransition>
    ) : (
      singlePanel
    );

  return (
    <div className="flex w-full shrink-0 flex-col items-center gap-5 lg:w-auto lg:items-start">
      <div
        role={selectedView.id === "preview" ? "img" : undefined}
        aria-label={selectedView.id === "preview" ? selectedAlt : undefined}
        className={cn(
          "relative mx-auto w-64 sm:w-80 lg:mx-0 lg:w-104",
          hasPreview && "aspect-[25/18]",
        )}
      >
        {selectedView.id === "preview" ? (
          <>
            <img
              src={selectedView.panels[1].url}
              alt=""
              aria-hidden
              className={cn(
                "pointer-events-none absolute top-1/2 left-[34%] z-10 aspect-square w-[66%] -translate-y-1/2 rounded-full object-contain shadow-2xl",
                // Out from behind the case when the project opens, and not again
                // each time the reader switches back to the preview.
                selectedViewId === undefined && "animate-disc-slide-out motion-reduce:animate-none",
              )}
              onError={() => markFailed(selectedView.panels[1].id)}
            />
            <ProjectCoverTransition desktopProjectId={project.desktopProjectId}>
              <img
                src={selectedView.panels[0].url}
                alt=""
                aria-hidden
                className="relative z-20 block aspect-square w-[72%] rounded-[2px] object-contain shadow-[0_25px_50px_-12px_rgb(0_0_0/0.7)] ring-1 ring-black/25"
                onError={() => markFailed(selectedView.panels[0].id)}
              />
            </ProjectCoverTransition>
          </>
        ) : (
          isolatedPiece
        )}
      </div>

      {/* A switch that has nowhere else to go is absent rather than disabled. */}
      {views.length > 1 && (
        <ul aria-label={t("project.artwork.label")} className="flex flex-wrap gap-2">
          {views.map((view) => {
            const label = t(view.labelKey);
            const isSelected = view.id === selectedView.id;
            return (
              <li key={view.id}>
                <button
                  type="button"
                  aria-label={t("project.artwork.show", { artwork: label })}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedViewId(view.id)}
                  className={cn(
                    "flex items-center gap-2 rounded-full py-1 pr-3 pl-1 ring-1 backdrop-blur-sm",
                    interactiveMotionClassName,
                    focusRingClassName,
                    isSelected
                      ? "bg-foreground/10 ring-primary"
                      : "ring-border/70 hover:bg-foreground/5 hover:ring-muted-foreground",
                  )}
                >
                  <span className="relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background/40">
                    {view.id === "preview" && (
                      <img
                        src={view.panels[1].url}
                        alt=""
                        loading="lazy"
                        className="absolute top-1/2 left-[34%] size-[66%] -translate-y-1/2 rounded-full object-contain"
                      />
                    )}
                    <img
                      src={view.panels[0].url}
                      alt=""
                      loading="lazy"
                      className={cn(
                        "object-cover",
                        view.id === "preview" ? "absolute left-0 size-[72%]" : "size-full",
                      )}
                    />
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      "text-xs leading-none font-medium",
                      isSelected ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
